/**
 * The block pattern itself: a paramedic's block plus a date, turned into the
 * shift they are working (PLAN.md §3.1, `CLAUDE.md` "The roster engine").
 *
 * An 8-day cycle, `AM → D → PM → N → off ×4`, run by eight blocks each offset
 * one day from the last. On any date exactly four blocks are working — one on
 * each shift — and four are off.
 *
 *     cycleDay = floorMod(dayNumber(date) − dayNumber(anchorDate) − blockIndex, cycleLength)
 *     0→AM · 1→D · 2→PM · 3→N · 4–7→off
 *
 * **The invariant that must not break:** the cycle position is taken with
 * `floorMod`, never `%`. The `− blockIndex` term is negative for blocks 3B, 4A
 * and 4B *on the anchor date itself* (`0 − 5`, `0 − 6`, `0 − 7`), so negative
 * intermediates arise inside the anchor week itself — three cells of the
 * golden fixture, not a distant edge case. `%` takes the sign of its
 * left operand, so it would index `cycleDayShifts` with a negative number and
 * hand back a wrong shift — silently, and looking exactly like a right one.
 * `src/engine/__tests__/roster.test.ts` pins all 64 cells of `brief.md`'s
 * table against this; three of them fail the moment `%` appears here.
 *
 * **This module imports nothing from `src/data/`.** The roster arrives as a
 * `RosterConfig` argument, which is what makes "the anchor is configuration,
 * not a constant" literally true and keeps the `data/ → engine/` dependency
 * one-way.
 */

import { addDays, dayNumber, diffDays, floorMod, addMonths } from './civil'
import type {
  BlockCode,
  DateRange,
  IsoDate,
  RosterConfig,
  ShiftCode,
  ShiftInstance,
} from './types'

/**
 * A block's position in the configured order, which *is* its offset in days
 * from the anchor block. `1A → 0` … `4B → 7`.
 *
 * Throws rather than returning `-1` for an unknown code: a block that is not
 * on the roster is a bug upstream, and `-1` would quietly shift the whole
 * cycle by one day and produce a plausible, wrong roster for every date.
 */
export function blockIndex(block: BlockCode, config: RosterConfig): number {
  const index = config.blockOrder.indexOf(block)
  if (index < 0) {
    throw new RangeError(
      `Not a roster block: ${JSON.stringify(block)} (expected one of ${config.blockOrder.join(', ')})`,
    )
  }
  return index
}

/**
 * Where in the 8-day cycle this block sits on this date, `0` … `cycleLength-1`.
 *
 * The one place the formula lives. Everything else in the app that asks "what
 * are they working?" is this function wearing a different hat.
 */
export function cycleDay(date: IsoDate, block: BlockCode, config: RosterConfig): number {
  const offset = dayNumber(date) - dayNumber(config.anchorDate) - blockIndex(block, config)
  // floorMod, never `%`. See the module header.
  return floorMod(offset, config.cycleLength)
}

/**
 * The shift a cycle day maps to, or `null` for one of the four off days.
 *
 * Throws on a cycle day outside the configured table rather than returning
 * `undefined`, which would type-launder its way into a `ShiftInstance` and
 * surface much later as a shift with no code.
 */
export function shiftCodeForCycleDay(day: number, config: RosterConfig): ShiftCode | null {
  if (!Number.isInteger(day) || day < 0 || day >= config.cycleDayShifts.length) {
    throw new RangeError(
      `No such cycle day: ${day} (the cycle has ${config.cycleDayShifts.length} days)`,
    )
  }
  return config.cycleDayShifts[day]
}

/**
 * Every shift the block works across `range`, **both ends inclusive**, in date
 * order. Off days produce no entry at all, so the result is the roster rather
 * than a calendar with holes in it.
 *
 * Each entry is `source: 'DERIVED'` and carries no `shiftId`: a block user's
 * roster is not stored, and a derived shift gets a database row only at the
 * moment it needs an identity — when it is advertised or swapped.
 *
 * An inverted range returns `[]` rather than throwing. Callers build ranges
 * from user-chosen months and horizons; an empty window is a legitimate
 * "nothing here", not a programming error.
 */
export function derivedBlockShifts(
  block: BlockCode,
  range: DateRange,
  config: RosterConfig,
): ShiftInstance[] {
  // Validate the block up front, so an unknown code is a loud error even when
  // the range turns out to be empty and the loop below never runs.
  blockIndex(block, config)

  const span = diffDays(range.to, range.from)
  if (span < 0) return []

  const instances: ShiftInstance[] = []
  let date = range.from
  for (let offset = 0; offset <= span; offset += 1) {
    const code = shiftCodeForCycleDay(cycleDay(date, block, config), config)
    if (code !== null) instances.push({ date, code, source: 'DERIVED' })
    date = addDays(date, 1)
  }
  return instances
}

/**
 * The window the roster is generated over — `PLAN.md` A7, three months back
 * and six months forward by default.
 *
 * `today` is an **argument**, never read from the clock. Reading the clock in
 * here is precisely the impurity the engine boundary exists to prevent: it
 * would make every test of everything downstream depend on the day it ran.
 */
export function horizonRange(today: IsoDate, config: RosterConfig): DateRange {
  return {
    from: addMonths(today, -config.horizon.monthsBack),
    to: addMonths(today, config.horizon.monthsForward),
  }
}
